<template>
    <div class="biz-team-panel">
        <div class="team-header">
            <h2 class="team-title">专业团队</h2>
            <p class="team-subtitle">汇聚税务领域资深专家，为您提供专业可靠的税务服务</p>
        </div>

        <a-spin :spinning="loading" class="team-spin">
            <div v-if="loading" class="skeleton-grid">
                <div v-for="n in 6" :key="'sk-' + n" class="skeleton-card">
                    <div class="skeleton-avatar"></div>
                    <div class="skeleton-line w-50"></div>
                    <div class="skeleton-line w-70"></div>
                    <div class="skeleton-line w-90"></div>
                    <div class="skeleton-line w-60"></div>
                </div>
            </div>
            <div v-else-if="teamList.length === 0" class="empty-state">
                <UserOutlined style="font-size: 48px; color: #d9d9d9;" />
                <p>暂无团队成员信息</p>
            </div>
            <div v-else class="team-grid">
                <a-card
                    v-for="expert in teamList"
                    :key="expert.id"
                    class="expert-card"
                    hoverable
                    @click="showDetail(expert)"
                >
                    <div class="expert-card-inner">
                        <div class="expert-avatar-wrapper">
                            <a-avatar :size="64" class="expert-avatar">
                                <template #icon><UserOutlined /></template>
                            </a-avatar>
                        </div>
                        <div class="expert-name">{{ expert.name }}</div>
                        <div class="expert-title-tag">{{ expert.title }}</div>

                        <div class="expert-badges">
                            <a-tag
                                v-for="(q, i) in qualificationList(expert.qualification)"
                                :key="'q-' + i"
                                color="blue"
                            >
                                {{ q }}
                            </a-tag>
                            <a-tag
                                v-if="expert.experience_years && expert.experience_years > 0"
                                color="orange"
                            >
                                {{ expert.experience_years }}年
                            </a-tag>
                        </div>

                        <div class="expert-expertise">
                            <span
                                v-for="(e, i) in expertiseList(expert.expertise)"
                                :key="'e-' + i"
                                class="expertise-tag"
                            >
                                {{ e }}
                            </span>
                        </div>

                        <div class="expert-intro-clamp">
                            {{ expert.intro || '暂无简介' }}
                        </div>
                    </div>
                </a-card>
            </div>
        </a-spin>

        <!-- 详情弹窗 -->
        <a-modal
            v-model:visible="detailVisible"
            :title="selectedExpert ? selectedExpert.name : ''"
            :footer="null"
            width="560px"
            destroy-on-close
        >
            <template v-if="selectedExpert">
                <div class="detail-header">
                    <a-avatar :size="72" class="detail-avatar">
                        <template #icon><UserOutlined /></template>
                    </a-avatar>
                    <div class="detail-header-info">
                        <div class="detail-name">{{ selectedExpert.name }}</div>
                        <div class="detail-title">{{ selectedExpert.title }}</div>
                    </div>
                </div>

                <a-divider style="margin: 16px 0;" />

                <div class="detail-section">
                    <div class="detail-section-title">资质证书</div>
                    <div class="detail-tags">
                        <a-tag
                            v-for="(q, i) in qualificationList(selectedExpert.qualification)"
                            :key="'dq-' + i"
                            color="blue"
                        >
                            {{ q }}
                        </a-tag>
                    </div>
                </div>

                <div class="detail-section" v-if="selectedExpert.experience_years">
                    <div class="detail-section-title">从业年限</div>
                    <div class="detail-text">{{ selectedExpert.experience_years }} 年</div>
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">擅长领域</div>
                    <div class="detail-tags">
                        <span
                            v-for="(e, i) in expertiseList(selectedExpert.expertise)"
                            :key="'de-' + i"
                            class="detail-expertise-tag"
                        >
                            {{ e }}
                        </span>
                    </div>
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">联系方式</div>
                    <div class="detail-contact">
                        <PhoneOutlined /> {{ selectedExpert.phone || '-' }}
                    </div>
                    <div class="detail-contact">
                        <MailOutlined /> {{ selectedExpert.email || '-' }}
                    </div>
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">个人简介</div>
                    <div class="detail-intro">{{ selectedExpert.intro || '暂无简介' }}</div>
                </div>
            </template>
        </a-modal>
    </div>
</template>

<script>
import {
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
} from '@ant-design/icons-vue';
import { ref, onMounted } from 'vue';
import { expertTeamList } from '../../../api/service.js';

export default {
    name: 'BizTeamPanel',
    components: {
        UserOutlined,
        PhoneOutlined,
        MailOutlined,
    },
    setup() {
        const loading = ref(true);
        const teamList = ref([]);
        const detailVisible = ref(false);
        const selectedExpert = ref(null);

        const fetchTeamList = async () => {
            loading.value = true;
            try {
                const response = await expertTeamList();
                if (response.code === 200 && response.data) {
                    teamList.value = Array.isArray(response.data) ? response.data : [];
                } else {
                    teamList.value = [];
                }
            } catch (e) {
                console.error('获取专业团队列表失败', e);
                teamList.value = [];
            }
            loading.value = false;
        };

        const showDetail = (expert) => {
            selectedExpert.value = expert;
            detailVisible.value = true;
        };

        // 资质证书分割
        const qualificationList = (val) => {
            if (!val) return [];
            return val.split(/[,，、]/).filter(Boolean);
        };

        // 擅长领域分割
        const expertiseList = (val) => {
            if (!val) return [];
            return val.split(/[,，、]/).filter(Boolean);
        };

        onMounted(() => {
            fetchTeamList();
        });

        return {
            loading,
            teamList,
            detailVisible,
            selectedExpert,
            showDetail,
            qualificationList,
            expertiseList,
        };
    },
};
</script>

<style scoped>
.biz-team-panel {
    padding: 24px 0;
}

.team-header {
    text-align: center;
    margin-bottom: 40px;
}

.team-title {
    font-size: 28px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 8px;
}

.team-subtitle {
    font-size: 14px;
    color: #999;
    margin: 0;
}

.team-spin {
    min-height: 200px;
}

/* 骨架屏 */
.skeleton-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
}

.skeleton-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 32px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.skeleton-card .skeleton-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-card .skeleton-line {
    height: 14px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-card .skeleton-line.w-50 {
    width: 50%;
}

.skeleton-card .skeleton-line.w-60 {
    width: 60%;
}

.skeleton-card .skeleton-line.w-70 {
    width: 70%;
}

.skeleton-card .w-90 {
    width: 90%;
}

@keyframes skeleton-pulse {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

/* 空状态 */
.empty-state {
    text-align: center;
    padding: 80px 0;
}

.empty-state p {
    margin-top: 16px;
    font-size: 16px;
    color: #999;
}

/* 网格布局 */
.team-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
}

.expert-card {
    border-radius: 12px;
    transition: all 0.3s;
    cursor: pointer;
}

.expert-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.expert-card :deep(.ant-card-body) {
    padding: 24px 20px;
}

.expert-card-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.expert-avatar-wrapper {
    margin-bottom: 10px;
}

.expert-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    font-size: 24px;
}

.expert-name {
    font-size: 17px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 3px;
}

.expert-title-tag {
    font-size: 13px;
    color: #1890ff;
    margin-bottom: 10px;
}

.expert-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-bottom: 8px;
}

.expert-badges :deep(.ant-tag) {
    font-size: 11px;
    padding: 0 8px;
    line-height: 22px;
    border-radius: 4px;
}

.expert-expertise {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-bottom: 10px;
}

.expertise-tag {
    font-size: 12px;
    color: #666;
    background: #f5f5f5;
    padding: 2px 10px;
    border-radius: 12px;
}

/* 简介限制 2 行 */
.expert-intro-clamp {
    font-size: 12px;
    color: #999;
    line-height: 1.5;
    text-align: center;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
}

/* ===== 弹窗样式 ===== */
.detail-header {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 8px 0 4px;
}

.detail-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    font-size: 28px;
    flex-shrink: 0;
}

.detail-header-info {
    flex: 1;
}

.detail-name {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 4px;
}

.detail-title {
    font-size: 14px;
    color: #1890ff;
}

.detail-section {
    margin-bottom: 16px;
}

.detail-section-title {
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
}

.detail-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.detail-tags :deep(.ant-tag) {
    font-size: 12px;
    padding: 0 10px;
    line-height: 24px;
    border-radius: 4px;
}

.detail-expertise-tag {
    font-size: 13px;
    color: #666;
    background: #f5f5f5;
    padding: 3px 12px;
    border-radius: 14px;
}

.detail-contact {
    font-size: 14px;
    color: #555;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.detail-text {
    font-size: 14px;
    color: #555;
}

.detail-intro {
    font-size: 14px;
    color: #555;
    line-height: 1.7;
    white-space: pre-wrap;
}
</style>
